import React from 'react';
import { TP_ORDER } from '../../patient-treatment/config';
import type { TpTreatmentPitch } from '../../patient-treatment/types';
import { Icon } from '../Icon/Icon';

export interface PatientTreatmentPitchCardProps {
  tx: TpTreatmentPitch;
  last?: boolean;
}

export function PatientTreatmentPitchCard({ tx, last }: PatientTreatmentPitchCardProps) {
  const m = TP_ORDER[tx.order];

  return (
    <div className="tc-rail" style={{ paddingBottom: last ? 0 : 16 }}>
      <span
        className="tc-rail-marker"
        style={{
          borderColor: m.color,
          background: tx.order === 'lead' ? m.color : 'var(--cream-1)',
        }}
        aria-hidden
      />
      <div className="tc-card px-[22px] py-[18px]">
        <div className="mb-[11px] flex flex-wrap items-center gap-2">
          <span
            className="inline-flex items-center gap-1.5 rounded-[var(--r-pill)] px-2.5 py-[3px] text-[10px] font-bold uppercase tracking-[0.05em]"
            style={{ background: m.tint, color: m.color }}
          >
            <Icon kind={m.icon} size={11} stroke={2.2} />
            {m.label}
          </span>
          <span className="tc-mono rounded-[5px] bg-[var(--surface-mute)] px-[7px] py-0.5 text-[10.5px] font-semibold text-[var(--ink-2)]">
            {tx.code}
          </span>
          {tx.tooth ? (
            <span className="tc-mono rounded-[5px] bg-[var(--gold-tint)] px-[7px] py-0.5 text-[10.5px] font-semibold text-[var(--gold)]">
              {tx.tooth}
            </span>
          ) : null}
          {tx.declineHistory.length > 0 ? (
            <span className="inline-flex items-center gap-1 rounded-[var(--r-pill)] bg-[var(--terracotta-tint)] px-2.5 py-0.5 text-[10.5px] font-semibold text-[var(--terracotta)]">
              <Icon kind="x" size={10} stroke={2.4} />
              Declined {tx.declineHistory.length}×
            </span>
          ) : null}
        </div>

        <h4 className="mb-2 font-[var(--font-display)] text-[17px] font-semibold leading-[1.3] tracking-[-0.02em] text-[var(--ink-0)]">
          {tx.description}
        </h4>

        <div className="tc-mono mb-4 flex flex-wrap items-center gap-2 text-xs">
          <span className="text-[var(--ink-3)]">
            Fee <strong className="text-[var(--ink-1)]">~${tx.fee.toLocaleString()}</strong>
          </span>
          <span className="text-[var(--ink-4)]">·</span>
          <span className="text-[var(--ink-3)]">
            Insurance <strong className="text-[var(--ink-1)]">~${tx.insurance}</strong>
          </span>
          <span className="text-[var(--ink-4)]">·</span>
          <span className="text-[13px] font-semibold text-[var(--success)]">
            He pays ~${tx.patientOOP.toLocaleString()}
          </span>
          {tx.checkedWith !== undefined ? (
            <>
              <span className="text-[var(--ink-4)]">·</span>
              <span className="text-[var(--ink-4)]">{tx.checkedWith}</span>
            </>
          ) : null}
          <span className="text-[var(--ink-4)]">·</span>
          <span className="text-[var(--ink-4)]">{tx.diagnosedAt}</span>
        </div>

        {tx.xrayTint !== undefined ? (
          <div className="mb-4 flex items-center gap-3">
            <div
              className="flex h-[52px] w-[68px] shrink-0 items-center justify-center rounded-[var(--r-md)] border border-[var(--cream-2)]"
              style={{ background: tx.xrayTint }}
              aria-hidden
            >
              <Icon kind="image" size={18} stroke={1.8} style={{ color: 'var(--ink-4)' }} />
            </div>
            {tx.xrayCaption !== undefined ? (
              <span className="text-[11.5px] text-[var(--ink-4)]">{tx.xrayCaption}</span>
            ) : null}
          </div>
        ) : null}

        <div className="flex flex-col gap-[13px]">
          <div>
            <span className="tc-eyebrow mb-1.5 block">Why this position</span>
            <p className="m-0 text-[13px] leading-[1.6] tracking-[-0.005em] text-[var(--ink-2)]">
              {tx.whyThisPosition}
            </p>
          </div>
          <div className="rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-0)] px-3.5 py-3">
            <span className="tc-eyebrow mb-1.5 block">How to frame it</span>
            <p className="m-0 text-[13px] leading-[1.65] tracking-[-0.005em] text-[var(--ink-1)]">
              {tx.howToFrame}
            </p>
          </div>
          {tx.practiceApproach ? (
            <div
              className="rounded-[var(--r-md)] border px-3.5 py-3"
              style={{
                background: 'var(--gold-tint)',
                borderColor: 'color-mix(in srgb, var(--gold) 28%, transparent)',
              }}
            >
              <div className="mb-1.5 flex items-center gap-1.5">
                <Icon kind="book" size={11} stroke={1.9} style={{ color: 'var(--gold)' }} />
                <span className="tc-eyebrow text-[var(--gold)]">Practice playbook</span>
              </div>
              <p className="m-0 text-[13px] leading-[1.6] tracking-[-0.005em] text-[var(--ink-1)]">
                {tx.practiceApproach}
              </p>
            </div>
          ) : null}
          {tx.doctorNote ? (
            <blockquote className="m-0 border-l-2 border-[var(--ai-3)] pl-3.5 text-[13.5px] italic leading-[1.55] text-[var(--ink-2)]">
              &ldquo;{tx.doctorNote}&rdquo;
              {tx.doctorNoteBy ? (
                <div className="mt-1.5 not-italic">
                  <span className="tc-eyebrow text-[var(--ai-3)]">— {tx.doctorNoteBy}</span>
                </div>
              ) : null}
            </blockquote>
          ) : null}
          {tx.declineHistory.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {tx.declineHistory.map((d) => (
                <span
                  key={`${d.date}-${d.reason}`}
                  className="inline-flex items-center gap-1.5 rounded-[var(--r-pill)] bg-[var(--terracotta-tint)] px-2.5 py-0.5 text-[10.5px] font-semibold text-[var(--terracotta)]"
                >
                  {d.date} · {d.reason}
                </span>
              ))}
            </div>
          ) : null}
          <details className="tc-why">
            <summary>
              <Icon kind="pulse" size={12} style={{ color: 'var(--ink-4)' }} />
              Clinical &ldquo;why now&rdquo;
            </summary>
            <p className="mt-1.5 border-l border-[var(--cream-2)] pl-3 text-[12.5px] leading-[1.55] text-[var(--ink-3)]">
              {tx.clinicalNow}
            </p>
          </details>
        </div>
      </div>
    </div>
  );
}
